import { useEffect, useMemo, useCallback } from 'react';
import { useDictStore } from '@/stores/dictStore';
import { dictToOptions, dictToLabelMap } from '@/utils/dict';

/**
 * 单字典 Hook 返回接口
 */
export interface UseDictReturn {
  /** 当前字典项列表 */
  items: Api.Dict.PublicDictItemResponse[];
  /** 加载中状态 */
  loading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 下拉框 Options 数组 [{ label, value, ... }] */
  options: ReturnType<typeof dictToOptions>;
  /** Key-Value 映射 (item_code -> item_name) */
  labelMap: Record<string, string>;
  /** 根据 itemCode 获取 item_name */
  getLabel: (itemCode?: string, fallback?: string) => string;
  /** 根据 itemCode 获取 item_value */
  getValue: (itemCode?: string, fallback?: string) => string;
  /** 重新获取/刷新当前字典 */
  reload: () => Promise<Api.Dict.PublicDictItemResponse[]>;
}

/**
 * 封装在组件内部快捷使用单一字典的 React Hook
 *
 * @param typeCode 字典类型编码 (如 'PROJECT_STATUS')
 * @param autoFetch 是否在组件挂载时自动请求，默认为 true
 */
export function useDict(typeCode: string, autoFetch = true): UseDictReturn {
  const dictMap = useDictStore((state) => state.dictMap);
  const loadingMap = useDictStore((state) => state.loadingMap);
  const errorMap = useDictStore((state) => state.errorMap);
  const fetchDict = useDictStore((state) => state.fetchDict);
  const clearDictCache = useDictStore((state) => state.clearDictCache);

  const items = useMemo(() => {
    return (typeCode && dictMap[typeCode]) || [];
  }, [typeCode, dictMap]);

  const loading = useMemo(() => {
    return Boolean(typeCode && loadingMap[typeCode]);
  }, [typeCode, loadingMap]);

  const error = useMemo(() => {
    return (typeCode && errorMap[typeCode]) || null;
  }, [typeCode, errorMap]);

  useEffect(() => {
    if (autoFetch && typeCode) {
      fetchDict(typeCode).catch(() => {});
    }
  }, [typeCode, autoFetch, fetchDict]);

  const reload = useCallback(async () => {
    if (!typeCode) return [];
    clearDictCache(typeCode);
    return fetchDict(typeCode);
  }, [typeCode, clearDictCache, fetchDict]);

  const getLabel = useCallback(
    (itemCode?: string, fallback = '') => {
      if (!itemCode) return fallback;
      const target = items.find((item) => item.item_code === itemCode);
      return target?.item_name || fallback || itemCode;
    },
    [items]
  );

  const getValue = useCallback(
    (itemCode?: string, fallback = '') => {
      if (!itemCode) return fallback;
      const target = items.find((item) => item.item_code === itemCode);
      return target?.item_value || fallback;
    },
    [items]
  );

  const options = useMemo(() => dictToOptions(items), [items]);
  const labelMap = useMemo(() => dictToLabelMap(items), [items]);

  return {
    items,
    loading,
    error,
    options,
    labelMap,
    getLabel,
    getValue,
    reload
  };
}

/**
 * 批量加载多个字典的 Hook
 *
 * @param typeCodes 字典类型编码数组 (如 ['TYPE_A', 'TYPE_B'])
 */
export function useDicts(typeCodes: string[]) {
  const dictMap = useDictStore((state) => state.dictMap);
  const loadingMap = useDictStore((state) => state.loadingMap);
  const fetchDict = useDictStore((state) => state.fetchDict);

  useEffect(() => {
    if (Array.isArray(typeCodes) && typeCodes.length > 0) {
      typeCodes.forEach((code) => {
        if (code) {
          fetchDict(code).catch(() => {});
        }
      });
    }
  }, [typeCodes, fetchDict]);

  const loading = useMemo(() => {
    return typeCodes.some((code) => Boolean(loadingMap[code]));
  }, [typeCodes, loadingMap]);

  const dicts = useMemo(() => {
    const result: Record<string, Api.Dict.PublicDictItemResponse[]> = {};
    typeCodes.forEach((code) => {
      result[code] = dictMap[code] || [];
    });
    return result;
  }, [typeCodes, dictMap]);

  return {
    dicts,
    loading
  };
}
